import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { AlertTriangle, TrendingUp, MapPin, Clock, Thermometer, CheckCircle2, Loader2 } from 'lucide-react';
import { Progress } from '@/shared/components/ui/progress';

const API_URL = API_BASE;

const RiskBadge = ({ risk }) => {const { t } = useTranslation("ezparcel");
  const riskConfig = {
    cold_chain_excursion: { label: 'Cold Chain Risk', color: 'bg-red-100 text-red-700 border-red-200', icon: Thermometer },
    sla_risk: { label: 'SLA Risk', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Clock },
    capacity_risk: { label: 'Capacity Risk', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: TrendingUp },
    delay_risk: { label: 'Delay Risk', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle }
  };

  const config = riskConfig[risk] || riskConfig.delay_risk;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>);

};

export const LiveDispatchBoard = () => {const { t } = useTranslation("ezparcel");
  const [data, setData] = useState({ activeRoutes: [], rerouteProposals: [] });
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);

  useEffect(() => {
    const fetchDispatchData = async () => {
      try {
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_URL}/api/ezparcel/dispatch-board`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setData({
            activeRoutes: result.routes || [],
            rerouteProposals: result.unassigned || []
          });
        }
      } catch (err) {
        console.error('Failed to fetch dispatch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDispatchData();
  }, []);

  const { activeRoutes, rerouteProposals } = data;
  const attentionRequiredCount = activeRoutes.filter((r) => r.attentionRequired).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("activeRoutes", "Active Routes")}</p>
                <p className="text-2xl font-bold">{activeRoutes.length}</p>
              </div>
              <MapPin className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("attentionRequired", "Attention Required")}</p>
                <p className="text-2xl font-bold text-red-600">{attentionRequiredCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("coldChainActive", "Cold Chain Active")}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {activeRoutes.reduce((sum, r) => sum + r.coldChainParcels, 0)}
                </p>
              </div>
              <Thermometer className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("pendingApprovals", "Pending Approvals")}</p>
                <p className="text-2xl font-bold text-orange-600">{rerouteProposals.length}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reroute Proposals */}
      {rerouteProposals.length > 0 &&
      <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />{t("pendingRerouteApprovals", "Pending Reroute Approvals")}

          </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rerouteProposals.map((proposal) =>
          <div key={proposal.id} className="p-4 bg-white border border-orange-200 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">{proposal.routeId}</h4>
                      <Badge className="bg-orange-100 text-orange-700">
                        {proposal.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">{t("reason", "Reason:")}</span> {proposal.reason}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">{t("proposedAction", "Proposed Action:")}</span> {proposal.proposedAction}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t("timeImpact", "Time Impact")}</p>
                    <p className="font-semibold text-sm">{proposal.impact.timeDelay}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t("costImpact", "Cost Impact")}</p>
                    <p className="font-semibold text-sm">{proposal.impact.costIncrease}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t("slaRisk", "SLA Risk")}</p>
                    <p className="font-semibold text-sm text-green-600">{proposal.impact.slaRisk}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t("coldChainRisk", "Cold Chain Risk")}</p>
                    <p className="font-semibold text-sm text-green-600">{proposal.impact.coldChainRisk}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="h-4 w-4 mr-2" />{t("approveReroute", "Approve Reroute")}

              </Button>
                  <Button size="sm" variant="outline">{t("viewDetails", "View Details")}

              </Button>
                  <Button size="sm" variant="outline" className="text-red-600">{t("reject", "Reject")}

              </Button>
                </div>
              </div>
          )}
          </CardContent>
        </Card>
      }

      {/* Active Routes Board */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("liveRoutesMonitor", "Live Routes Monitor")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activeRoutes.map((route) => {
              const progress = route.completedStops / route.totalStops * 100;
              const hasRisks = route.riskFlags && route.riskFlags.length > 0;

              return (
                <div
                  key={route.id}
                  className={`p-4 rounded-lg border transition-all ${
                  hasRisks ?
                  'border-red-200 bg-red-50' :
                  'border-border hover:shadow-md'} ${
                  selectedRoute === route.id ? 'ring-2 ring-[hsl(var(--accent))]' : ''}`}
                  onClick={() => setSelectedRoute(route.id)}>
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{route.name}</h4>
                        <Badge className="status-badge status-in-progress">
                          {route.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {route.attentionRequired &&
                        <Badge className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{t("actionRequired", "Action Required")}

                        </Badge>
                        }
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{t("driver", "Driver:")}{route.driver}</span>
                        <span>•</span>
                        <span>{t("vehicle", "Vehicle:")}{route.vehicle}</span>
                        {route.coldChainParcels > 0 &&
                        <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-blue-600">
                              <Thermometer className="h-3 w-3" />
                              {route.coldChainParcels}{t("coldChain", "Cold Chain")}
                          </span>
                          </>
                        }
                      </div>
                    </div>
                  </div>

                  {/* Risk Flags */}
                  {hasRisks &&
                  <div className="flex flex-wrap gap-2 mb-3">
                      {route.riskFlags.map((risk, idx) =>
                    <RiskBadge key={idx} risk={risk} />
                    )}
                    </div>
                  }

                  {/* Progress */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t("routeProgress", "Route Progress")}</span>
                      <span className="font-medium">
                        {route.completedStops}/{route.totalStops}{t("stops", "stops")}
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{t("eta", "ETA:")}{route.eta}</span>
                      {route.delays > 0 &&
                      <span className="text-orange-600 font-medium">
                          +{route.delays}{t("minDelay", "min delay")}
                      </span>
                      }
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <MapPin className="h-4 w-4 mr-2" />{t("liveMap", "Live Map")}

                    </Button>
                    {hasRisks &&
                    <Button variant="outline" size="sm" className="text-orange-600">{t("proposeReroute", "Propose Reroute")}

                    </Button>
                    }
                    <Button variant="outline" size="sm">{t("contactDriver", "Contact Driver")}

                    </Button>
                  </div>
                </div>);

            })}
          </div>
        </CardContent>
      </Card>
    </div>);

};